import { LitElement, html, css, ifDefined } from '~/lib/bn-lit-element';
import { sharedStyles } from '../styles/shared-styles';
import './bn-radio-input';

let nextDataListId = 1;

class BnFormField extends LitElement {
  static get properties() {
    return {
      autocomplete: { type: String },
      checked: { type: Boolean },
      cols: { type: Number },
      disabled: { type: Boolean },
      max: { type: String },
      min: { type: String },
      name: { type: String },
      options: { type: Array },
      placeholder: { type: String },
      readonly: { type: Boolean },
      required: { type: Boolean },
      requireOption: { type: Boolean, attribute: 'require-option' },
      rows: { type: Number },
      select: { type: Boolean },
      step: { type: String },
      title: { type: String },
      type: { type: String },
      value: { type: String },
      missing: { type: Boolean, reflect: true },
      highlightErrors: { type: Boolean, attribute: 'highlight-errors', reflect: true },
      currentError: { type: String },
      validator: { type: Function },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        --error-color: var(--black-base);
      }
      input, slot[name="label"] {
        transition: border-color .2s, color .2s, box-shadow .2s;
      }
      .error slot[name="label"] {
        color: var(--error-color);
        font-weight: bold;
      }
      .error input {
        box-shadow: 0 0 3px var(--error-color);
        border: 1px solid var(--error-color);
      }
      :host([highlight-errors]) {
        --error-color: var(--red-20);
      }
      :host([highlight-errors]) .error-text {
        display: initial;
      }
      textarea {
        resize: var(--textarea-resize, auto);
      }
      .error-text {
        color: var(--red-20);
        font-weight: bold;
        font-size: small;
        display: none;
      }
      * {
        height: var(--bn-form-field-height, auto);
        min-height: var(--bn-form-field-min-height, auto);
        max-height: var(--bn-form-field-max-height, auto);
      }
    `];
  }

  render() {
    const {
      checked,
      disabled,
      max,
      min,
      name,
      options,
      placeholder = '',
      readonly,
      required,
      step,
      title,
      type = 'text',
      value = '',
    } = this;
    switch (type.toLowerCase()) {
    case 'radio':
      return html`
          <bn-radio-input
            name="${name}"
            ?highlight-errors="${this.highlightErrors}"
            .values="${value}"
            .title="${title}"
            ?checked="${checked}"
            ?required="${required}"
            @input="${this._onAnyChange}"
          ></bn-radio-input>
        `;

    case 'textarea':
      return this.renderField(html`
          <textarea
            .name="${name}"
            ?disabled="${disabled}"
            ?readonly="${readonly}"
            ?required="${required}"
            .placeholder="${placeholder}"
            .rows="${this.rows}"
            .cols="${this.cols}"
            @change="${this._onAnyChange}"
            @keyup="${this._onAnyChange}"
            @paste="${this._onAnyChange}"
            @cut="${this._onAnyChange}"
            @blur="${this._onBlur}"
          >${value}</textarea>
        `);

    case 'hidden':
      return html`
          <input
            .type="${type}"
            .name="${name}"
            .value="${value}"
          />
        `;

    case 'checkbox':
      return this.renderField(html`
          <input
            .type="${type}"
            .name="${name}"
            .value="${value}"
            ?disabled="${disabled}"
            ?readonly="${readonly}"
            ?checked="${checked}"
            @change="${this._onAnyChange}"
            @blur="${this._onBlur}"
          />
        `);

    case 'date':
    case 'datetime-local':
      return this.renderField(html`
          <input
            .type="${type}"
            .name="${name}"
            .value="${value}"
            ?disabled="${disabled}"
            ?readonly="${readonly}"
            ?required="${required}"
            max="${ifDefined(max)}"
            min="${ifDefined(min)}"
            step="${step || 'any'}"
            @change="${this._onAnyChange}"
            @keyup="${this._onAnyChange}"
            @paste="${this._onAnyChange}"
            @cut="${this._onAnyChange}"
            @blur="${this._onBlur}"
          />
        `);

    case 'number':
      return this.renderField(html`
          <input
            .type="${type}"
            .name="${name}"
            .value="${value}"
            ?disabled="${disabled}"
            ?readonly="${readonly}"
            ?required="${required}"
            .placeholder="${placeholder}"
            .max="${max}"
            .min="${min}"
            @focus="${this._focus}" step="${step || 'any'}"
            @change="${this._onAnyChange}"
            @keyup="${this._onAnyChange}"
            @paste="${this._onAnyChange}"
            @cut="${this._onAnyChange}"
            @blur="${this._onBlur}"
          />
        `);

    case 'tel':
      return this.renderField(html`
          <input
            .type="${type}"
            .name="${name}"
            .value="${value}"
            ?disabled="${disabled}"
            ?readonly="${readonly}"
            ?required="${required}"
            .placeholder="${placeholder}"
            pattern="[+ \(\)0-9\-\.]*"
            @focus="${this._focus}"
            @change="${this._onAnyChange}"
            @keyup="${this._onAnyChange}"
            @paste="${this._onAnyChange}"
            @cut="${this._onAnyChange}"
            @blur="${this._onBlur}"
          />
        `);

    case 'text':
      return this.renderText({ type, name, value, disabled, options, readonly, required, placeholder });
    default:
      return this.renderField(html`
          <input
            .type="${type}"
            .name="${name}"
            .value="${value}"
            ?disabled="${disabled}"
            ?readonly="${readonly}"
            ?required="${required}"
            .placeholder="${placeholder}"
            @change="${this._onAnyChange}"
            @keyup="${this._onAnyChange}"
            @paste="${this._onAnyChange}"
            @cut="${this._onAnyChange}"
            @blur="${this._onBlur}"
          />
        `);
    }
  }

  renderText({ type, name, value, disabled, readonly, required, placeholder, options }) {
    let datalistId;
    let datalist = '';

    if (options) {
      datalistId = `${name}-datalist-${nextDataListId++}`;
      const optionsHtml = options.map((opt) => {
        return html`<option value="${opt.value}"/>`;
      });
      datalist = html`
        <datalist id="${datalistId}">
            ${optionsHtml}
        </datalist>
      `;
    }

    // list cannot use a property accessor as it is read-only in HTMLInputElement
    return this.renderField(html`
      <input
        .type="${type}"
        .name="${name}"
        .value="${value}"
        ?disabled="${disabled}"
        ?readonly="${readonly}"
        ?required="${required}"
        .placeholder="${placeholder}"
        list="${ifDefined(datalistId)}"
        @focus="${this._focus}"
        @change="${this._onAnyChange}"
        @keyup="${this._onAnyChange}"
        @paste="${this._onAnyChange}"
        @cut="${this._onAnyChange}"
        @blur="${this._onBlur}"
        autocomplete="${this.autocomplete}"
      />
      ${datalist}
    `);
  }

  _focus() {
    if (this.select || (this.select === undefined && this.options)) {
      this.input.select();
    }
  }

  renderField(input) {
    const {
      title,
      name,
      required,
      currentError,
    } = this;
    const label = title !== undefined ? title : name;

    const errorMarkup = currentError ? html`<span class="error-text">${currentError}</span>` : '';
    return html`
      <div class="form-field ${currentError ? 'error' : ''}">
        <label title="${label} ${required ? '(required)' : ''}">
          <slot name="label">
            ${label}${required ? html`<span class="required-label">*</span>` : html``}
          </slot>
          <div class="input-wrapper">
            ${input}
            <slot name="suffix"></slot>
          </div>
        </label>
        ${errorMarkup}
      </div>
    `;
  }

  firstUpdated() {
    this._onAnyChange();
  }

  _onBlur() {
    this.highlightErrors = true;
    this._onAnyChange();
  }

  _onAnyChange() {
    // Hack to get around cut/paste events being onBefore events
    setTimeout(() => {
      const oldError = this.currentError;
      const newError = this._checkValidity();

      const currentValue = this.input.value;
      if (this.value === currentValue && oldError === newError) {
        return;
      }

      this.dispatchEvent(new CustomEvent('form-data', { detail: this }));
    }, 0);
  }

  _checkValidity() {
    const currentValue = this.input.value;
    if (this._lastCheckedValue === currentValue) {
      return;
    }

    this._lastCheckedValue = currentValue;

    const errors = [];

    const missing = this.required && (currentValue === '' || currentValue === undefined);
    if (missing) {
      errors.push('Please enter a value');
    }

    if (this.requireOption) {
      const options = this.options || [];
      if (!options.find((o) => o.value === currentValue)) {
        errors.push('Please select one of the provided options');
      }
    }

    if (!errors.length && this.validator) {
      const error = this.validator(currentValue);
      error && errors.push(error);
    }

    // this might be something we want to consider using ValidityState for in the future
    [this.currentError] = errors;
    return this.currentError;
  }

  get input() {
    return this.shadowRoot.querySelector('input,bn-radio-input,textarea');
  }
}

window.customElements.define('bn-form-field', BnFormField);
